/**
 * @file file-drop-mixin.ts
 *
 * Conditionally enables drag-and-drop file import on the host element.
 * Follows the 'file-droppable' attribute, at connect and whenever it is
 * toggled. Dropped files are announced with source 'app' on the shared drop
 * event, for downstream handlers.
 *
 * Exposes: _enableFileDrop, _disableFileDrop
 */
import { DragDropHandler, DropOverlay, sendDroppedFiles } from '../../../features/importExport/import/fileBase.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by FileDropMixin. */
export interface FileDropMixinMembers {
  /** Activates drag-and-drop file import on the host element. */
  _enableFileDrop(): void;
  /** Deactivates drag-and-drop file import. */
  _disableFileDrop(): void;
}

export const FileDropMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class FileDropMixin extends Base {
    _appDragDropHandler: DragDropHandler | null = null;
    _appDropOverlay: DropOverlay | null = null;

    async connectedCallback() {
      await super.connectedCallback?.();
      if (this.hasAttribute('file-droppable')) this._enableFileDrop();
    }

    disconnectedCallback() {
      this._disableFileDrop();
      super.disconnectedCallback?.();
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
      super.attributeChangedCallback?.(name, oldValue, newValue);
      if (name !== 'file-droppable') return;
      newValue !== null ? this._enableFileDrop() : this._disableFileDrop();
    }

    _enableFileDrop(): void {
      if (this._appDragDropHandler) return;
      this._appDropOverlay = new DropOverlay(this.shadowRoot!);
      this._appDragDropHandler = new DragDropHandler(
        this,
        (files) => { sendDroppedFiles(this, 'app', files); },
        this._appDropOverlay,
        false,
      );
    }

    _disableFileDrop(): void {
      this._appDragDropHandler?.destroy();
      this._appDragDropHandler = null;
      this._appDropOverlay?.remove();
      this._appDropOverlay = null;
    }
  };
